import { createContext, useEffect, useState } from 'react';

import * as authService from '../services/auth.service';
import {
  getFullName,
  getRoleLabel,
  isAdminRole,
  isTeacherRole,
  normalizeRole,
} from '../utils/auth';

export const AuthContext = createContext();

// Helper functions to manage session in localStorage
const persistSession = (setUser, data) => {
  setUser(data.user);
  localStorage.setItem('user', JSON.stringify(data.user));
  localStorage.setItem('token', data.access_token);
  localStorage.setItem('refreshToken', data.refresh_token);
};

const clearSession = (setUser) => {
  setUser(null);
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
};

const syncStoredUser = () => {
  const storedUser = localStorage.getItem('user');
  if (!storedUser) return null;

  try {
    return JSON.parse(storedUser);
  } catch {
    localStorage.removeItem('user');
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => syncStoredUser());
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    setUser(syncStoredUser());
    setAuthLoading(false);
  }, []);

  // Login
  const login = async (data) => {
    const res = await authService.login(data);

    const role = normalizeRole(res?.user?.role);
    if (![1, 2].includes(role)) {
      throw new Error('Invalid user role');
    }

    persistSession(setUser, { ...res, user: { ...res.user, role } });
    return res;
  };

  // Register
  const register = async (data) => {
    const res = await authService.register(data);
    const role = normalizeRole(res?.user?.role);
    if (res?.user && ![1, 2].includes(role)) {
      throw new Error('Invalid user role');
    }
    return res;
  };

  // Verify email
  const verifyEmail = async (email, code) => {
    const res = await authService.verifyEmail({ email, code });
    const role = normalizeRole(res?.user?.role);
    if (res?.user && ![1, 2].includes(role)) {
      throw new Error('Invalid user role');
    }
    persistSession(setUser, res);
    return res;
  };

  // Resend OTP
  const resendOtp = async (email, type) => {
    return authService.resendOtp({ email, type });
  };

  // Forgot password
  const forgotPassword = async (email) => {
    return authService.forgotPassword({ email });
  };

  // Reset password
  const resetPassword = async (data) => {
    return authService.resetPassword(data); // { email, code, password, password_confirmation }
  };

  // Logout
  const logout = async () => {
    try {
      await authService.logout();
    } catch (e) {
      console.error('Logout failed', e);
    }
    clearSession(setUser);
  };

  // Change password
  const changePassword = async (data) => {
    const res = await authService.changePassword(data);
    clearSession(setUser);
    return res;
  };

  // Send OTP to new address
  const initiateEmailChange = async (data) => {
    return authService.initiateEmailChange(data);
  };

  // Change email
  const confirmEmailChange = async (code) => {
    const res = await authService.confirmEmailChange({ code });
    if (user) {
      const updatedUser = { ...user, email: res.email ?? user.email };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
    return res;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        authLoading,
        isAuthenticated: !!user,
        role: normalizeRole(user?.role),
        roleLabel: getRoleLabel(user?.role),
        fullName: getFullName(user),
        isAdmin: isAdminRole(user?.role),
        isTeacher: isTeacherRole(user?.role),
        login,
        register,
        logout,
        verifyEmail,
        resendOtp,
        forgotPassword,
        resetPassword,
        changePassword,
        initiateEmailChange,
        confirmEmailChange,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
